import { Modal as MainModal } from '@mui/material'
import { AvatarSizeProps } from 'components/avatar'
import {
  Box,
  Button,
  Flex,
  Image,
  FormWrap,
  TextAsProps,
  Text,
  ButtonShowAsProps,
  ColorProps,
} from 'components/elements'
import { h4Styles } from 'components/elements/styleText'
import { BackwardIcon, Close } from 'components/icons'
import { ShimmerCss, StatusCss, TextShimmer } from 'components/shimmers'
import { ShimmerIcon } from 'components/shimmers/avatar'
import { SvgWrap } from 'components/svg'
import { t } from 'i18next'
import { forwardRef, ReactNode, RefObject } from 'react'
import { styled, CSS } from 'theme'
import { scrollBarThin } from 'theme/globalStyleCss'

interface ModalProps {
  modalSize?: AvatarSizeProps | 'xxl'
  open: boolean
  setOpen: (open: boolean) => void
  onAccept?: () => void
  onClose?: () => void
  onOptional?: () => void
  css?: CSS
  bodyCss?: CSS
  HeaderCss?: CSS
  FooterCss?: CSS
  title?: {
    as?: TextAsProps
    icon?: ReactNode
    imageUrl?: string
    imageCss?: CSS
    text: any
    description?: string | ReactNode
    type?: ReactNode
    hideBgImage?: boolean
    textCss?: CSS
  }
  isForm?: boolean
  button?:
    | {
        cancelText?: string
        acceptText?: string
        optionalText?: string
        optinalColor?: ColorProps
        optinalTransparent?: ButtonShowAsProps
        optionalIcon?: ReactNode
        onOptionalCancel?: () => void
        headerButton?: ReactNode
        cancelDisabled?: boolean
        acceptDisabled?: boolean
        optionalDisabled?: boolean
      }
    | undefined
  children?: ReactNode
  headerChildren?: ReactNode
  hideHeader?: boolean
  hideFooter?: boolean
  backToLevel?: {
    show?: boolean
    onClicked?: () => void
  }
  ref?: RefObject<HTMLDivElement>
  isAcceptLoading?: boolean
  isOptionalLoading?: boolean
  loading?: boolean
  enablePrompt?: boolean
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      modalSize,
      open,
      setOpen,
      onClose,
      onOptional,
      css,
      bodyCss,
      HeaderCss,
      FooterCss,
      children,
      isForm,
      button,
      title,
      headerChildren,
      onAccept,
      hideHeader,
      hideFooter,
      backToLevel,
      isAcceptLoading,
      isOptionalLoading,
      loading,
      enablePrompt,
    },
    ref
  ) => {
    const {
      acceptText,
      cancelText,
      onOptionalCancel,
      headerButton,
      cancelDisabled,
      optionalIcon,
      optinalColor,
      optinalTransparent,
      acceptDisabled,
      optionalText,
      optionalDisabled,
    } = button || {}
    const {
      as,
      icon,
      imageUrl,
      imageCss,
      text = '',
      description,
      type,
      hideBgImage,
      textCss,
    } = title || {}
    const { show, onClicked } = backToLevel || {}
    // const ref = useRef(null)
    const handleClose = () => {
      setOpen(false)
      onClose?.()
    }

    const hasDescription = Object?.prototype?.hasOwnProperty?.call(
      title,
      'description'
    )
    const hasIcon = Object?.prototype?.hasOwnProperty?.call(title, 'imageUrl')

    const renderModalHeader = () => (
      <ModalHeader
        align="center"
        justify="between"
        css={{
          ...HeaderCss,
        }}
        className="modal-header"
      >
        {headerButton
          ? headerButton
          : icon &&
            (loading ? (
              <ShimmerIcon
                variant="square"
                size="lg"
                css={{ mr: '$space$20', ...ShimmerCss }}
              />
            ) : (
              <>
                <SvgWrap size="sm" color="gray" css={{ ...ImageBoxCss }}>
                  {icon}
                </SvgWrap>
                {!hideBgImage && (
                  <Image
                    className="backgroundImage"
                    size="auto"
                    url="/assets/icons/backgroundRing.png"
                    css={ImageBgOverlayCss}
                  />
                )}
              </>
            ))}
        <Flex direction="column" css={{ flexGrow: 1 }}>
          <Flex>
            {hasIcon &&
              (loading ? (
                <ShimmerIcon
                  variant="square"
                  size="lg"
                  css={{ mr: '$space$20', ...ShimmerCss }}
                />
              ) : (
                <Image
                  size={modalSize || 'xl'}
                  url={imageUrl || ''}
                  border
                  css={{ mr: '$space$20', ...imageCss }}
                />
              ))}
            <Flex wrap="wrap">
              {loading ? (
                <TextShimmer />
              ) : (
                <Text as={as || 'h5'} css={{ color: '$heading', ...textCss }}>
                  {text}
                </Text>
              )}
              {!!type &&
                (loading ? (
                  <Box
                    css={{ ...ShimmerCss, ...StatusCss, ml: '$space$15' }}
                  ></Box>
                ) : (
                  type
                ))}
              {hasDescription &&
                (loading ? (
                  <TextShimmer css={{ width: '100%', mb: 0 }} />
                ) : (
                  <Text as="p" css={{ color: '$heading', width: '100%' }}>
                    {description}
                  </Text>
                ))}
            </Flex>
          </Flex>
          {show && !loading && (
            <Button
              type="button"
              transparent="asIconPrimary"
              css={{
                mt: '$space$5',
                justifyContent: 'flex-start',
                ...h4Styles,
              }}
              startIcon={<BackwardIcon />}
              onClick={onClicked}
            >
              {t('back_to_configurator')}
            </Button>
          )}
        </Flex>
        <Flex align="center">
          {headerChildren}
          <Button
            size="sm"
            color="transparent"
            transparent="asIcon"
            type="button"
            onClick={handleClose}
          >
            <SvgWrap color="gray" size="sm">
              <Close />
            </SvgWrap>
          </Button>
        </Flex>
      </ModalHeader>
    )
    const renderModalBody = () => (
      <>
        <ModalBody
          className="modal-body"
          css={{
            'flexDirection': 'column',
            'maxHeight': !hideFooter ? 'calc(100vh - 16px)' : '',
            'overflow': 'auto',
            'p': '$space$20',
            '&.modal-body': {
              ...bodyCss,
            },
            ...scrollBarThin,
          }}
        >
          {children}
        </ModalBody>
      </>
    )
    const renderModalFooter = () => (
      <ModalFooter
        justify="start"
        gap="12"
        css={{ ...FooterCss, borderBottom: 0 }}
        className="footer-modal"
      >
        <Flex gap="12" justify="end" grow={1}>
          {cancelText && (
            <Button
              size="md"
              transparent="asTransparentGray"
              type="button"
              onClick={!!onOptionalCancel ? onOptionalCancel : handleClose}
              disabled={cancelDisabled}
            >
              {cancelText || 'Accept'}
            </Button>
          )}
          {acceptText && (
            <Button
              size="md"
              type={isForm ? 'submit' : 'button'}
              onClick={onAccept}
              disabled={acceptDisabled}
              {...(isAcceptLoading && {
                loading: {
                  enable: true,
                  text: acceptText || 'Accept',
                },
              })}
            >
              {acceptText || 'Decline'}
            </Button>
          )}
        </Flex>
        {optionalText && (
          <Button
            size="md"
            type={isForm ? 'submit' : 'button'}
            onClick={onOptional}
            disabled={optionalDisabled}
            {...(optinalTransparent
              ? { transparent: optinalTransparent }
              : { color: 'error' })}
            {...(optinalColor ? { color: optinalColor } : undefined)}
            {...(optionalIcon ? { startIcon: optionalIcon } : undefined)}
            {...(isOptionalLoading
              ? {
                  loading: {
                    enable: true,
                    text: optionalText || 'Accept',
                  },
                }
              : {})}
          >
            {optionalText || 'Decline'}
          </Button>
        )}
      </ModalFooter>
    )

    return (
      <ModalCss
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        ref={ref}
        disablePortal={true}
      >
        {isForm ? (
          <ModalForm
            css={{ ...css, width: '100%' }}
            onSubmit={onAccept}
            size={modalSize}
          >
            {renderModalHeader()}
            {children && renderModalBody()}
            {renderModalFooter()}
          </ModalForm>
        ) : (
          <ChildrenCSS css={{ ...css }} size={modalSize}>
            {!hideHeader ? renderModalHeader() : null}
            {children && renderModalBody()}
            {!hideFooter ? renderModalFooter() : null}
          </ChildrenCSS>
        )}
      </ModalCss>
    )
  }
)

const ImageBoxCss = {
  // p: '$space$12',
  border: '1px solid $border',
  borderRadius: '$radii$md',
  mr: '$space$16',
}

export const ImageBgOverlayCss = {
  position: 'absolute',
  left: 0,
  top: 0,
  zIndex: -1,
}

export const ModalFooterCss = {
  p: '$space$24',
  borderTop: '1px solid',
  borderColor: '$border',
}

export const ModalCss = styled(MainModal, {})

export const ModalHeader = styled(Flex, {
  position: 'relative',
  p: '$space$20',
  borderBottom: '1px solid',
  borderColor: '$border',
  overflow: 'hidden',
})

export const ModalBody = styled(Flex, {
  p: '$space$20',
  flex: 1,
})

export const ModalFooter = styled(Flex, {
  p: '$space$20',
  borderTop: '1px solid',
  borderColor: '$border',
  left: '20px',
  right: '20px',
})

export const ChildrenCSS = styled(Box, {
  position: 'absolute',
  width: '100%',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  background: '$whiteBase',
  borderRadius: '$radii$md',
  boxShadow: '$modalBoxShadow',

  variants: {
    size: {
      xxs: {
        maxWidth: 300,
      },
      xs: {
        maxWidth: 300,
      },
      sm: {
        maxWidth: 400,
      },
      md: {
        maxWidth: 500,
      },
      lg: {
        maxWidth: 640,
      },
      xl: {
        maxWidth: 1024,
      },
      xxl: {
        'maxWidth': 1400,
        'width': 'calc(100% - 60px)',
        '@media (min-width: 1600px)': {
          maxWidth: 1540,
        },
      },
      login: {
        maxWidth: '66.666667%',
        height: '100vh',
        px: '$space$50',
        borderRadius: 0,
        left: 'initial',
        transform: 'none',
        top: 0,
        right: 0,
      },
      level1: {
        'maxWidth': '90%',
        'height': '100vh',
        'px': '$space$20',
        'borderRadius': 0,
        'left': 'initial',
        'transform': 'none',
        'top': 0,
        'right': 0,
        '.modal-header': {
          px: 0,
        },
        '.modal-body': {
          p: 0,
          py: '$space$20',
          width: 'calc(100% + 12px)',
          pr: '$space$12',
        },
        '@media (min-width: 1600px)': {
          maxWidth: '85%',
          px: '$space$40',
        },
        '.footer-modal': {
          borderTop: 'none',
          px: 0,
        },
      },
      level2: {
        'maxWidth': '85%',
        'height': '100vh',
        'px': '$space$20',
        'borderRadius': 0,
        'left': 'initial',
        'transform': 'none',
        'top': 0,
        'right': 0,
        '.modal-header': {
          px: 0,
        },
        '.modal-body': {
          p: 0,
          py: '$space$20',
          width: 'calc(100% + 12px)',
          pr: '$space$12',
        },
        '@media (min-width: 1600px)': {
          maxWidth: '75%',
          px: '$space$40',
        },
        '.footer-modal': {
          borderTop: 'none',
          px: 0,
        },
      },
      level3: {
        'maxWidth': '80%',
        'height': '100vh',
        'px': '$space$20',
        'borderRadius': 0,
        'left': 'initial',
        'transform': 'none',
        'top': 0,
        'right': 0,
        '.modal-header': {
          px: 0,
        },
        '.modal-body': {
          p: 0,
          py: '$space$20',
          width: 'calc(100% + 12px)',
          pr: '$space$12',
        },
        '@media (min-width: 1600px)': {
          maxWidth: '65%',
          px: '$space$40',
        },
        '.footer-modal': {
          borderTop: 'none',
          px: 0,
        },
      },
      level4: {
        'maxWidth': '60%',
        'height': '100vh',
        'px': '$space$20',
        'borderRadius': 0,
        'left': 'initial',
        'transform': 'none',
        'top': 0,
        'right': 0,
        '.modal-header': {
          px: 0,
        },
        '.modal-body': {
          p: 0,
          py: '$space$20',
          width: 'calc(100% + 12px)',
          pr: '$space$12',
        },
        '@media (min-width: 1600px)': {
          maxWidth: '45%',
          px: '$space$40',
        },
        '.footer-modal': {
          borderTop: 'none',
          px: 0,
        },
      },
      level5: {
        'maxWidth': '40%',
        'height': '100vh',
        'px': '$space$20',
        'borderRadius': 0,
        'left': 'initial',
        'transform': 'none',
        'top': 0,
        'right': 0,
        '.modal-header': {
          px: 0,
        },
        '.modal-body': {
          p: 0,
          py: '$space$20',
          width: 'calc(100% + 12px)',
          pr: '$space$12',
        },
        '@media (min-width: 1600px)': {
          maxWidth: '40%',
          px: '$space$40',
        },
        '.footer-modal': {
          px: 0,
        },
      },
      container: {
        top: 30,
        transform: 'translate(-50%, 0)',
        maxWidth: 1620,
      },
    },
  },
  defaultVariants: {
    size: 'sm',
  },
})

export const ModalForm = styled(FormWrap, ChildrenCSS, {})
